// Applies the admin-edited site meta (GET /api/meta, backend game/meta) to the
// live <head>. index.html ships the defaults; this overwrites them at boot.
// Client-side only — crawlers that do not run JS still see index.html's tags
// (TODO(owner): README › Admin › Open, TD-1). Google Tags are separate (utils/gtag.ts).

export interface SiteMeta {
  title: string;
  description: string;
  siteName: string;
  url: string;
  locale: string;
  image: string;
  imageAlt: string;
  twitterCard: string;
  /** Uploaded favicon URL per slot ('' = not uploaded). */
  icons: Record<string, string>;
}

// rel + sizes per backend slot (game/meta/meta.types.ts ICON_SLOTS).
const ICON_LINKS: Record<string, { rel: string; sizes: string; type?: string }> = {
  icon_ico: { rel: 'icon', sizes: 'any' },
  icon_png_16: { rel: 'icon', sizes: '16x16', type: 'image/png' },
  icon_png_32: { rel: 'icon', sizes: '32x32', type: 'image/png' },
  icon_png_192: { rel: 'icon', sizes: '192x192', type: 'image/png' },
  icon_png_180: { rel: 'apple-touch-icon', sizes: '180x180' },
};

function setMeta(doc: Document, attr: 'name' | 'property', key: string, content: string) {
  if (!content) return;
  let el = doc.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = doc.createElement('meta');
    el.setAttribute(attr, key);
    doc.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export function applySiteMeta(doc: Document, m: SiteMeta): void {
  if (m.title) doc.title = m.title;
  setMeta(doc, 'name', 'description', m.description);
  setMeta(doc, 'property', 'og:title', m.title);
  setMeta(doc, 'property', 'og:site_name', m.siteName);
  setMeta(doc, 'property', 'og:description', m.description);
  setMeta(doc, 'property', 'og:url', m.url);
  setMeta(doc, 'property', 'og:locale', m.locale);
  setMeta(doc, 'property', 'og:image', m.image);
  setMeta(doc, 'property', 'og:image:alt', m.image ? m.imageAlt : '');
  setMeta(doc, 'name', 'twitter:title', m.title);
  setMeta(doc, 'name', 'twitter:description', m.description);
  setMeta(doc, 'name', 'twitter:image', m.image);
  // A card without an image must degrade to `summary` or Twitter renders nothing.
  setMeta(doc, 'name', 'twitter:card', m.image ? m.twitterCard : '');

  const uploaded = Object.entries(m.icons ?? {}).filter(([slot, url]) => url && ICON_LINKS[slot]);
  if (!uploaded.length) return; // keep index.html's shipped icons
  doc.head.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]').forEach((el) => el.remove());
  for (const [slot, url] of uploaded) {
    const spec = ICON_LINKS[slot];
    const link = doc.createElement('link');
    link.rel = spec.rel;
    link.setAttribute('sizes', spec.sizes);
    if (spec.type) link.type = spec.type;
    link.href = url;
    doc.head.appendChild(link);
  }
}
